import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../lib/language'

export default function ContactPage() {
  const { t, language } = useLanguage()
  const isEs = language === 'es'
  const [scrolled, setScrolled]     = useState(false)
  const [showTop, setShowTop]       = useState(false)
  const [form, setForm]             = useState({ name: '', email: '', phone: '', subject: '', message: '' })
  const [submitted, setSubmitted]   = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState('')

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60)
      setShowTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }) }
  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: 'nancy@loanclosingagent.com',
          from_name: `LCA Contact Form — ${form.name}`,
          subject: `[LCA Contact] ${form.subject} — from ${form.name}`,
          message: `New contact form submission!!\n\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || 'Not provided'}\nSubject: ${form.subject}\n\nMessage:\n${form.message}\n\n---\nReply directly to: ${form.email}`,
        })
      })
      if (!response.ok) throw new Error('Send failed')
      setSubmitted(true)
    } catch (err) {
      setError(isEs
        ? 'Hubo un error al enviar tu mensaje. Por favor llámanos al (904) 865-0133.'
        : 'There was an error sending your message. Please call us at (904) 865-0133.')
    } finally {
      setSubmitting(false)
    }
  }

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }

    /* ── ADA ── */
    .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
    .lca-skip { position:absolute; top:-100px; left:16px; background:#C9A84C; color:#0D1B3E; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
    .lca-skip:focus { top:16px; }
    :focus-visible { outline:2px solid #C9A84C; outline-offset:3px; border-radius:4px; }

    /* ── NAV ── */
    .lca-nav { position:fixed; top:0; left:0; right:0; z-index:100; padding:18px 48px; display:flex; align-items:center; justify-content:space-between; background:linear-gradient(to bottom,rgba(0,0,0,0.7),transparent); transition:background 0.4s; }
    .lca-nav.scrolled { background:rgba(13,27,62,0.97); backdrop-filter:blur(8px); }
    .lca-nav-brand { display:flex; flex-direction:column; text-decoration:none; }
    .lca-nav-name { font-family:'Cormorant Garamond',serif; font-size:22px; font-weight:600; color:#C9A84C; letter-spacing:0.02em; line-height:1.2; }
    .lca-nav-sub { font-size:8px; color:rgba(201,168,76,0.55); letter-spacing:0.15em; margin-top:3px; }
    .lca-nav-links { display:flex; gap:28px; align-items:center; }
    .lca-nav-link { color:rgba(255,255,255,0.75); font-size:12px; letter-spacing:0.05em; text-decoration:none; transition:color 0.2s; }
    .lca-nav-link:hover { color:#C9A84C; }
    .lca-nav-link.active { color:#C9A84C; font-weight:500; }
    .lca-nav-signin { color:#C9A84C; font-size:12px; font-weight:500; background:transparent; padding:0; border:none; cursor:pointer; letter-spacing:0.08em; text-decoration:none; }
    @media(max-width:900px){ .lca-nav { padding:14px 24px; } .lca-nav-links { gap:18px; } .lca-nav-name { font-size:18px; } }
    @media(max-width:768px){ .lca-nav { padding:12px 20px; } .lca-nav-name { font-size:15px; } .lca-nav-sub { display:none; } .lca-nav-links { gap:12px; } .lca-nav-link { font-size:11px; } }
    @media(max-width:580px){ .lca-nav-name { font-size:13px; } .lca-nav-links .lca-nav-link { display:none; } .lca-nav-links { gap:10px; } }

    /* ── HERO ── */
    .lca-hero { padding:120px 48px 64px; text-align:center; border-bottom:1px solid rgba(255,255,255,0.06); }
    .lca-hero-eyebrow { font-size:10px; letter-spacing:0.2em; color:#C9A84C; margin-bottom:14px; }
    .lca-hero-rule { width:40px; height:1px; background:#C9A84C; margin:0 auto 22px; opacity:0.8; }
    .lca-hero h1 { font-family:'Cormorant Garamond',serif; font-size:40px; font-weight:400; color:#fff; margin-bottom:14px; line-height:1.15; }
    .lca-hero h1 span { color:#C9A84C; }
    .lca-hero-sub { font-size:14px; color:rgba(255,255,255,0.5); max-width:480px; margin:0 auto; line-height:1.85; }
    @media(max-width:640px){ .lca-hero { padding:100px 20px 48px; } .lca-hero h1 { font-size:26px; } }

    /* ── MAIN GRID ── */
    .lca-contact-wrap { max-width:1100px; margin:0 auto; padding:72px 48px; display:grid; grid-template-columns:1fr 1.5fr; gap:64px; align-items:start; }
    @media(max-width:860px){ .lca-contact-wrap { grid-template-columns:1fr; gap:48px; padding:48px 20px; } }

    /* ── LEFT ── */
    .lca-info-section { display:flex; flex-direction:column; gap:32px; }
    .lca-info-label { font-size:10px; letter-spacing:0.18em; color:#C9A84C; font-weight:500; margin-bottom:14px; }
    .lca-info-rule { width:20px; height:1px; background:#C9A84C; margin-bottom:16px; opacity:0.6; }
    .lca-contact-item { display:flex; align-items:flex-start; gap:14px; margin-bottom:16px; }
    .lca-contact-icon { width:40px; height:40px; border-radius:10px; background:rgba(201,168,76,0.08); border:1px solid rgba(201,168,76,0.15); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .lca-contact-icon svg { width:18px; height:18px; fill:#C9A84C; }
    .lca-contact-detail-label { font-size:11px; color:rgba(255,255,255,0.35); letter-spacing:0.06em; margin-bottom:3px; }
    .lca-contact-detail-value { font-size:14px; color:#fff; text-decoration:none; display:block; transition:color 0.2s; }
    a.lca-contact-detail-value:hover { color:#C9A84C; }
    .lca-hours { background:rgba(201,168,76,0.04); border:1px solid rgba(201,168,76,0.15); border-radius:12px; padding:20px 22px; }
    .lca-hours-row { display:flex; justify-content:space-between; align-items:center; padding:7px 0; border-bottom:1px solid rgba(255,255,255,0.05); font-size:13px; }
    .lca-hours-row:last-child { border-bottom:none; }
    .lca-hours-day { color:rgba(255,255,255,0.55); }
    .lca-hours-time { color:#C9A84C; font-weight:500; }
    .lca-hours-closed { color:rgba(255,255,255,0.3); font-style:italic; }
    .lca-online-badge { display:inline-flex; align-items:center; gap:8px; background:rgba(45,122,79,0.1); border:1px solid rgba(45,122,79,0.3); border-radius:20px; padding:6px 14px; font-size:12px; color:#2D7A4F; font-weight:500; margin-top:16px; }
    .lca-online-dot { width:7px; height:7px; border-radius:50%; background:#2D7A4F; animation:pulse 2s infinite; }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

    /* ── RIGHT — Form ── */
    .lca-form-title { font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:400; color:#fff; margin-bottom:6px; }
    .lca-form-sub { font-size:13px; color:rgba(255,255,255,0.4); margin-bottom:28px; line-height:1.7; }
    .lca-form { display:flex; flex-direction:column; gap:16px; }
    .lca-form-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
    @media(max-width:520px){ .lca-form-row { grid-template-columns:1fr; } }
    .lca-field { display:flex; flex-direction:column; gap:6px; }
    .lca-label { font-size:11px; font-weight:500; color:rgba(255,255,255,0.5); letter-spacing:0.06em; }
    .lca-req { color:#C9A84C; margin-left:2px; }
    .lca-input { background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); border-radius:8px; padding:11px 14px; font-size:14px; color:#fff; transition:border-color 0.2s, background 0.2s; outline:none; font-family:'DM Sans',sans-serif; }
    .lca-input::placeholder { color:rgba(255,255,255,0.2); }
    .lca-input:focus { border-color:#C9A84C; background:rgba(201,168,76,0.04); }
    .lca-textarea { resize:vertical; min-height:130px; }
    /* ✅ CTA PASS — gold gradient, white text, 10px radius, shadow, hover lift */
    .lca-submit { background:linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%); color:#fff; padding:14px 32px; border-radius:10px; font-size:13px; font-weight:600; border:none; cursor:pointer; letter-spacing:0.07em; box-shadow:0 4px 14px rgba(13,27,62,0.3); transition:transform 0.15s, box-shadow 0.2s; align-self:flex-start; }
    .lca-submit:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 6px 20px rgba(13,27,62,0.4); }
    .lca-submit:focus-visible { outline:3px solid #fff; outline-offset:3px; }
    .lca-submit:disabled { opacity:0.6; cursor:not-allowed; }
    @media(max-width:480px){ .lca-submit { width:100%; text-align:center; } }

    /* Error */
    .lca-error { background:rgba(192,57,43,0.08); border:1px solid rgba(192,57,43,0.3); border-radius:10px; padding:12px 16px; font-size:13px; color:#E74C3C; display:flex; gap:8px; align-items:flex-start; }

    /* Success */
    .lca-success { background:rgba(45,122,79,0.08); border:1px solid rgba(45,122,79,0.3); border-radius:14px; padding:40px 32px; text-align:center; }
    .lca-success-icon { font-size:48px; margin-bottom:16px; color:#2D7A4F; }
    .lca-success-title { font-family:'Cormorant Garamond',serif; font-size:26px; color:#fff; margin-bottom:10px; }
    .lca-success-sub { font-size:14px; color:rgba(255,255,255,0.5); line-height:1.8; margin-bottom:24px; }
    /* ✅ CTA PASS — success button gold gradient */
    .lca-success-btn { background:linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%); color:#fff; padding:12px 28px; border-radius:10px; font-size:12px; font-weight:600; border:none; cursor:pointer; letter-spacing:0.07em; text-decoration:none; display:inline-block; box-shadow:0 4px 14px rgba(13,27,62,0.3); transition:transform 0.15s, box-shadow 0.2s; }
    .lca-success-btn:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(13,27,62,0.4); }

    /* ── BACK TO TOP ── */
    .lca-back-top-wrap { background: #0D1B3E; display: flex; justify-content: center; padding: 16px 0 0; }
    .lca-back-top { position:fixed; bottom:24px; right:20px; width:44px; height:44px; border-radius:50%; background:#C9A84C; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:18px; color:#0D1B3E; font-weight:700; box-shadow:0 4px 16px rgba(0,0,0,0.3); transition:all 0.3s; opacity:0; pointer-events:none; z-index:150; }
    .lca-back-top.visible { opacity:1; pointer-events:auto; }
    .lca-back-top:hover { transform:translateY(-4px); box-shadow:0 6px 20px rgba(0,0,0,0.4); }

    /* ── FOOTER ── */
    .lca-footer { background:#0D1B3E; border-top:1px solid rgba(201,168,76,0.15); padding:32px 48px 28px; text-align:center; }
    .lca-footer-logo { display:inline-flex; align-items:center; gap:12px; text-decoration:none; margin-bottom:24px; }
    .lca-footer-logo-circle { width:48px; height:48px; border-radius:50%; border:1px solid rgba(201,168,76,0.3); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .lca-footer-logo-circle img { width:44px; height:44px; object-fit:contain; }
    .lca-footer-brand-name { font-family:'Cormorant Garamond',serif; font-size:15px; color:rgba(255,255,255,0.55); letter-spacing:0.04em; display:block; text-align:left; }
    .lca-footer-brand-sub { font-size:8px; color:rgba(201,168,76,0.45); letter-spacing:0.12em; margin-top:2px; display:block; text-align:left; }
    .lca-footer-links { display:flex; gap:24px; justify-content:center; flex-wrap:wrap; margin-bottom:24px; }
    .lca-footer-links a { font-size:11px; color:rgba(255,255,255,0.3); text-decoration:none; letter-spacing:0.04em; transition:color 0.2s; }
    .lca-footer-links a:hover { color:#C9A84C; }
    .lca-footer-bottom { border-top:1px solid rgba(255,255,255,0.06); padding-top:18px; }
    .lca-footer-copy { font-size:10px; color:rgba(255,255,255,0.2); margin-bottom:6px; }
    .lca-footer-veteran { font-size:11px; color:rgba(201,168,76,0.5); letter-spacing:0.05em; }
    @media(max-width:520px){ .lca-footer { padding:32px 20px 20px; } .lca-footer-links { gap:14px; } }
  `

  const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
  )
  const EmailIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
  )

  const hours = [
    { day_en: 'Monday – Friday', day_es: 'Lunes – Viernes', time: '9:00 AM – 6:00 PM EST', closed: false },
    { day_en: 'Saturday', day_es: 'Sábado', time: '', closed: true },
    { day_en: 'Sunday', day_es: 'Domingo', time: '', closed: true },
  ]

  return (
    <div style={{ background: '#0D1B3E', minHeight: '100vh', color: '#fff' }}>
      <style>{styles}</style>
      <a href="#contact-main" className="lca-skip">Skip to main content</a>

      {/* NAV */}
      <nav className={`lca-nav${scrolled ? ' scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <Link to="/" className="lca-nav-brand" aria-label="Loan Closing Agent LLC — Go to homepage">
          <span className="lca-nav-name">Loan Closing Agent LLC</span>
        </Link>
        <div className="lca-nav-links">
          <Link to="/" className="lca-nav-link">{isEs ? 'Inicio' : 'Home'}</Link>
          <Link to="/services" className="lca-nav-link">{t('nav_services')}</Link>
          <Link to="/about" className="lca-nav-link">{t('nav_about')}</Link>
          <Link to="/contact" className="lca-nav-link active">{t('nav_contact')}</Link>
          <Link to="/login" className="lca-nav-signin" aria-label={t('nav_signin')}>{t('nav_signin')}</Link>
        </div>
      </nav>

      <main id="contact-main" tabIndex={-1}>
        {/* HERO */}
        <section className="lca-hero" aria-labelledby="contact-heading">
          <div className="lca-hero-eyebrow" aria-hidden="true">{isEs ? 'ESTAMOS AQUÍ PARA AYUDARTE' : 'WE ARE HERE TO HELP'}</div>
          <div className="lca-hero-rule" aria-hidden="true" />
          <h1 id="contact-heading">
            {isEs ? 'Contáctanos' : 'Get in'} <span>{isEs ? 'Hoy' : 'Touch'}</span>
          </h1>
          <p className="lca-hero-sub">
            {isEs
              ? '¿Tienes preguntas? ¿Necesitas un notario? Estamos listos para ayudarte — llámanos, escríbenos o completa el formulario.'
              : 'Have questions? Need a notary? We\'re ready to help — call us, email us, or fill out the form below.'}
          </p>
        </section>

        {/* CONTACT GRID */}
        <div className="lca-contact-wrap">
          {/* LEFT — Info */}
          <div className="lca-info-section">
            <div>
              <div className="lca-info-label">{isEs ? 'INFORMACIÓN DE CONTACTO' : 'CONTACT INFORMATION'}</div>
              <div className="lca-info-rule" aria-hidden="true" />
              <div className="lca-contact-item">
                <div className="lca-contact-icon"><PhoneIcon /></div>
                <div>
                  <div className="lca-contact-detail-label">{isEs ? 'TELÉFONO' : 'PHONE'}</div>
                  <a href="tel:+19048650133" className="lca-contact-detail-value" aria-label="Call us at (904) 865-0133">(904) 865-0133</a>
                </div>
              </div>
              <div className="lca-contact-item">
                <div className="lca-contact-icon"><EmailIcon /></div>
                <div>
                  <div className="lca-contact-detail-label">{isEs ? 'CORREO ELECTRÓNICO' : 'EMAIL'}</div>
                  <a href="mailto:nancy@loanclosingagent.com" className="lca-contact-detail-value" aria-label="Email us">nancy@loanclosingagent.com</a>
                </div>
              </div>
            </div>
            {/* Hours */}
            <div>
              <div className="lca-info-label">{isEs ? 'HORARIO DE OFICINA' : 'OFFICE HOURS'}</div>
              <div className="lca-info-rule" aria-hidden="true" />
              <div className="lca-hours" role="list" aria-label={isEs ? 'Horario de oficina' : 'Office hours'}>
                {hours.map((h, i) => (
                  <div key={i} className="lca-hours-row" role="listitem">
                    <span className="lca-hours-day">{isEs ? h.day_es : h.day_en}</span>
                    {h.closed
                      ? <span className="lca-hours-closed">{isEs ? 'Cerrado' : 'Closed'}</span>
                      : <span className="lca-hours-time">{h.time}</span>}
                  </div>
                ))}
              </div>
              <div className="lca-online-badge" role="note">
                <div className="lca-online-dot" aria-hidden="true" />
                {isEs ? 'En línea 24/7/365' : 'Online 24/7/365'}
              </div>
              <div role="note" style={{ marginTop:12, fontSize:12, color:'rgba(255,255,255,0.5)', fontStyle:'italic', textAlign:'center' }}>
                {isEs
                  ? '📍 Brindando cobertura notarial móvil y remota en los 50 estados de EE. UU.'
                  : '📍 Providing mobile and remote notary coverage across all 50 U.S. states.'}
              </div>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div>
            {submitted ? (
              <div className="lca-success" role="alert" aria-live="assertive">
                <div className="lca-success-icon" aria-hidden="true">✓</div>
                <div className="lca-success-title">{isEs ? '¡Mensaje Enviado!' : 'Message Sent!'}</div>
                <p className="lca-success-sub">
                  {isEs
                    ? 'Gracias por contactarnos, Nancy se comunicará contigo dentro de 1 día hábil. ¡Que tengas un excelente día! 😊'
                    : 'Thank you for reaching out! Nancy will get back to you within 1 business day. Have a wonderful day! 😊'}
                </p>
                <button className="lca-success-btn" onClick={() => { setSubmitted(false); setForm({ name:'', email:'', phone:'', subject:'', message:'' }) }}>
                  {isEs ? 'Enviar otro mensaje' : 'Send another message'}
                </button>
              </div>
            ) : (
              <>
                <h2 className="lca-form-title">{isEs ? 'Envíanos un Mensaje' : 'Send Us a Message'}</h2>
                <p className="lca-form-sub">
                  {isEs
                    ? 'Completa el formulario y nos comunicaremos contigo dentro de 1 día hábil.'
                    : 'Fill out the form and we\'ll get back to you within 1 business day.'}
                </p>
                {error && (
                  <div className="lca-error" role="alert" aria-live="assertive" style={{ marginBottom: 16 }}>
                    <span aria-hidden="true">⚠️</span><span>{error}</span>
                  </div>
                )}
                <form className="lca-form" onSubmit={handleSubmit} noValidate aria-label={isEs ? 'Formulario de contacto' : 'Contact form'}>
                  <p style={{ fontSize:11, color:'rgba(255,255,255,0.3)', marginBottom:4 }}>
                    <span aria-hidden="true" style={{ color:'#C9A84C' }}>*</span> {isEs ? 'Campos requeridos' : 'Required fields'}
                  </p>
                  <div className="lca-form-row">
                    <div className="lca-field">
                      <label className="lca-label" htmlFor="contact-name">
                        {isEs ? 'Nombre completo' : 'Full name'}
                        <span className="lca-req" aria-hidden="true"> *</span>
                        <span className="sr-only">{isEs ? ' (requerido)' : ' (required)'}</span>
                      </label>
                      <input id="contact-name" className="lca-input" type="text" placeholder={isEs ? 'Tu nombre completo' : 'Your full name'} value={form.name} onChange={e => set('name', e.target.value)} required aria-required="true" />
                    </div>
                    <div className="lca-field">
                      <label className="lca-label" htmlFor="contact-email">
                        {isEs ? 'Correo electrónico' : 'Email address'}
                        <span className="lca-req" aria-hidden="true"> *</span>
                        <span className="sr-only">{isEs ? ' (requerido)' : ' (required)'}</span>
                      </label>
                      <input id="contact-email" className="lca-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => set('email', e.target.value)} required aria-required="true" />
                    </div>
                  </div>
                  <div className="lca-form-row">
                    <div className="lca-field">
                      <label className="lca-label" htmlFor="contact-phone">{isEs ? 'Teléfono' : 'Phone number'}</label>
                      <input id="contact-phone" className="lca-input" type="tel" placeholder="(xxx) xxx-xxxx" value={form.phone} onChange={e => set('phone', e.target.value)} />
                    </div>
                    <div className="lca-field">
                      <label className="lca-label" htmlFor="contact-subject">
                        {isEs ? 'Asunto' : 'Subject'}
                        <span className="lca-req" aria-hidden="true"> *</span>
                        <span className="sr-only">{isEs ? ' (requerido)' : ' (required)'}</span>
                      </label>
                      <select id="contact-subject" className="lca-input" value={form.subject} onChange={e => set('subject', e.target.value)} required aria-required="true" style={{ cursor:'pointer', background:'#0D1B3E', color:'#fff' }}>
                        <option value="" style={{ background:'#0D1B3E', color:'rgba(255,255,255,0.5)' }}>{isEs ? 'Selecciona un tema...' : 'Select a topic...'}</option>
                        <option value="request_notary" style={{ background:'#0D1B3E', color:'#fff' }}>{isEs ? 'Solicitar un Notario' : 'Request a Notary'}</option>
                        <option value="join_network" style={{ background:'#0D1B3E', color:'#fff' }}>{isEs ? 'Unirse a Nuestra Red' : 'Join Our Network'}</option>
                        <option value="company_services" style={{ background:'#0D1B3E', color:'#fff' }}>{isEs ? 'Servicios para Empresas' : 'Company Signing Services'}</option>
                        <option value="pricing" style={{ background:'#0D1B3E', color:'#fff' }}>{isEs ? 'Preguntas sobre Precios' : 'Pricing Questions'}</option>
                        <option value="general" style={{ background:'#0D1B3E', color:'#fff' }}>{isEs ? 'Pregunta General' : 'General Question'}</option>
                        <option value="veteran" style={{ background:'#0D1B3E', color:'#fff' }}>{isEs ? 'Descuento para Veteranos' : 'Veteran Discount'}</option>
                        <option value="other" style={{ background:'#0D1B3E', color:'#fff' }}>{isEs ? 'Otro' : 'Other'}</option>
                      </select>
                    </div>
                  </div>
                  <div className="lca-field">
                    <label className="lca-label" htmlFor="contact-message">
                      {isEs ? 'Mensaje' : 'Message'}
                      <span className="lca-req" aria-hidden="true"> *</span>
                      <span className="sr-only">{isEs ? ' (requerido)' : ' (required)'}</span>
                    </label>
                    <textarea id="contact-message" className="lca-input lca-textarea" placeholder={isEs ? '¿Cómo podemos ayudarte?' : 'How can we help you?'} value={form.message} onChange={e => set('message', e.target.value)} required aria-required="true" />
                  </div>
                  <button className="lca-submit" type="submit" disabled={submitting} aria-label={isEs ? "Enviar Mensaje" : "Send Message"} aria-busy={submitting}>
                    {submitting ? (isEs ? 'Enviando...' : 'Sending...') : (isEs ? 'Enviar Mensaje' : 'Send Message')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      {/* BACK TO TOP */}
      <div className="lca-back-top-wrap">
        <button className={`lca-back-top${showTop ? ' visible' : ''}`} onClick={scrollToTop} aria-label="Scroll back to top" aria-hidden={!showTop} tabIndex={showTop ? 0 : -1}>↑</button>
      </div>

      {/* FOOTER */}
      <footer className="lca-footer" role="contentinfo">
        <Link to="/" className="lca-footer-logo" aria-label="Loan Closing Agent LLC — Go to homepage">
          <div className="lca-footer-logo-circle">
            <img src="/lca_logo_quill_map.png" alt="Loan Closing Agent LLC logo" onError={e => { e.target.style.display = 'none' }} />
          </div>
          <div>
            <span className="lca-footer-brand-name">Loan Closing Agent LLC</span>
            <span className="lca-footer-brand-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </div>
        </Link>
        <nav className="lca-footer-links" aria-label="Footer navigation">
          <Link to="/">{isEs ? 'Inicio' : 'Home'}</Link>
          <Link to="/services">{t('footer_services')}</Link>
          <Link to="/about">{t('footer_about')}</Link>
          <Link to="/contact">{t('footer_contact')}</Link>
          <Link to="/accessibility">{t('footer_accessibility')}</Link>
          <Link to="/login">{t('footer_signin')}</Link>
        </nav>
        <div className="lca-footer-bottom">
          <div className="lca-footer-copy">{t('footer_copy')}</div>
          <div className="lca-footer-veteran">{t('footer_veteran')}</div>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.25)', marginTop:6, fontStyle:'italic' }}>
            Loan Closing Agent, LLC is not a law firm and does not provide legal advice.
          </div>
        </div>
      </footer>
    </div>
  )
}
